import { configureStore, isPlain } from '@reduxjs/toolkit';
import LogRocket from 'logrocket';
import { activeApiActionsReducer } from './activeApiActionsModel';
import { advisorAdminPortalReducer } from './advisorAdminPortalModel';
import { advisorBgaCarrierSettingsReducer } from './advisorBgaCarrierSettingsModel';
import { advisorHierarchyReducer } from './advisorHierarchyModel';
import { advisorReducer } from './advisorModel';
import { advisorOnboardingReducer } from './advisorOnboardingModel';
import { advisorOpportunitiesReducer } from './advisorOpportunitiesModel';
import { aiUnderwritingTypeformReducer } from './aiUnderwritingTypeformModel';
import { apiMiddleware } from './apiMiddleware';
import { appConfigFeatureFlagsReducer } from './appConfigFeatureFlagsModel';
import { clientEmailSliceReducer } from './clientEmailModel';
import { clientReducer } from './clientModel';
import { clientsReducer } from './clientsModel';
import { clientsSearchTextReducer } from './clientsSearchTextModel';
import { concurrentSearchSliceReducer } from './concurrentSearchModel';
import { consentReducer } from './consentModel';
import { dataTableReducer } from './dataTableModel';
import { emailPreferencesReducer } from './emailPreferencesModel';
import { illustrationSliceReducer } from './illustrationModel';
import { initialState } from './initialState';
import { invalidateClientMiddleware } from './invalidateClientMiddleware';
import { invalidateClientsMiddleWare } from './invalidateClientsMiddleware';
import { invalidateSubscriptionSeatsMiddleware } from './invalidateSubscriptionSeatsMiddleware';
import { invalidateAdvisorHierarchyMiddleware } from './invalidateAdvisorHierarchyMiddleware';
import { logTrackerReducer } from './logTrackerModel';
import { ltcFinancialPlanningReducer } from './ltcFinancialPlanningModel';
import { ltcPlanningQuotesReducer } from './ltcPlanningQuotesModel';
import { onboardingSlideTrackerReducer } from './onboardingSlideTrackerModel';
import { openAiCompletionSliceReducer } from './openAiCompletionModel';
import { openAiCompletionSearchSliceReducer } from './openAiCompletionSearchModel';
import { partnerPolicyApplicationModelReducer } from './partnerPolicyApplicationModel';
import { underwritingModelReducer } from './policyApplicationModel';
import { policyApplicationPrefillReducer } from './policyApplicationPrefillModel';
import { policyExtractorSliceReducer } from './policyExtractorModel';
import { quotesReducer } from './quotesModel';
import { resendTemporaryPasswordEmailReducer } from './resendTemporaryPasswordEmailModel';
import { invalidateSessionMiddleWare, sessionReducer } from './sessionModel';
import { sponsorshipMiddleware } from './sponsorshipMiddleware';
import { sponsorshipReducer } from './sponsorshipModel';
import { storeClientCalculationsMiddleware } from './storeClientCalculationsMiddleware';
import { enrichLtcPlanningQuotesMiddleware } from './enrichLtcPlanningQuotesMiddleware';
import { stripeSubscribeSessionReducer } from './stripeSubscribeSessionModel';
import subscriptionSeatsReducer from './subscriptionSeatsModel';
import { typeformPayloadReducer } from './typeformPayloadModel';
import { verifyPartnerLinkRequestReducer } from './verifyPartnerLinkRequestModel';
import { usageAnalyticsReducer } from './usageAnalyticsModel';

const defaultMiddlewareOptions = {
  serializableCheck: {
    isSerializable(value: unknown) {
      return value instanceof Date || value instanceof Error || isPlain(value);
    },
  },
};

const preloadedState = {
  clientsSearchText: localStorage.getItem('clientsSearchText') ?? '',
};

export const store = configureStore({
  reducer: {
    activeApiActions: activeApiActionsReducer,
    advisor: advisorReducer,
    client: clientReducer,
    clients: clientsReducer,
    clientsSearchText: clientsSearchTextReducer,
    logTracker: logTrackerReducer,
    session: sessionReducer,
    clientEmail: clientEmailSliceReducer,
    onboardingSlideTracker: onboardingSlideTrackerReducer,
    emailPreferences: emailPreferencesReducer,
    policyExtractor: policyExtractorSliceReducer,
    illustration: illustrationSliceReducer,
    dataTable: dataTableReducer,
    resendTemporaryPasswordEmail: resendTemporaryPasswordEmailReducer,
    verifyPartnerLinkRequest: verifyPartnerLinkRequestReducer,
    advisorHierarchy: advisorHierarchyReducer,
    advisorAdminPortal: advisorAdminPortalReducer,
    advisorOpportunities: advisorOpportunitiesReducer,
    sponsorship: sponsorshipReducer,
    subscriptionSeats: subscriptionSeatsReducer,
    stripeSubscribeSession: stripeSubscribeSessionReducer,
    quotes: quotesReducer,
    policyApplicationModel: underwritingModelReducer,
    openAiCompletion: openAiCompletionSliceReducer,
    openAiCompletionSearch: openAiCompletionSearchSliceReducer,
    concurrentSearch: concurrentSearchSliceReducer,
    partnerPolicyApplicationModel: partnerPolicyApplicationModelReducer,
    typeformPayload: typeformPayloadReducer,
    policyApplicationPrefill: policyApplicationPrefillReducer,
    aiUnderwritingTypeform: aiUnderwritingTypeformReducer,
    advisorBgaCarrierSettings: advisorBgaCarrierSettingsReducer,
    consent: consentReducer,
    advisorOnboarding: advisorOnboardingReducer,
    appConfigFeatureFlags: appConfigFeatureFlagsReducer,
    ltcFinancialPlanning: ltcFinancialPlanningReducer,
    ltcPlanningQuotes: ltcPlanningQuotesReducer,
    usageAnalytics: usageAnalyticsReducer,
  },
  middleware(getDefaultMiddleware) {
    return [
      apiMiddleware,
      enrichLtcPlanningQuotesMiddleware, // Must come after apiMiddleware to intercept responses
      invalidateClientsMiddleWare,
      invalidateClientMiddleware,
      invalidateSubscriptionSeatsMiddleware,
      invalidateAdvisorHierarchyMiddleware,
      storeClientCalculationsMiddleware,
      invalidateSessionMiddleWare,
      sponsorshipMiddleware,
      LogRocket.reduxMiddleware(),
    ].concat(getDefaultMiddleware(defaultMiddlewareOptions));
  },
  preloadedState,
});

export type AppState = typeof initialState;
